'use client'

import { X } from 'lucide-react'
import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

interface CodeBlockDialogProps {
  isOpen: boolean
  onClose: () => void
  onInsert: (code: string, language: string) => void
  onUpdate?: (code: string, language: string) => void
  initialCode?: string
  initialLanguage?: string
  isEditMode?: boolean
}

const supportedLanguages = [
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'python', label: 'Python' },
  { value: 'java', label: 'Java' },
  { value: 'cpp', label: 'C++' },
  { value: 'csharp', label: 'C#' },
  { value: 'php', label: 'PHP' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
  { value: 'swift', label: 'Swift' },
  { value: 'kotlin', label: 'Kotlin' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'scss', label: 'SCSS' },
  { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'XML' },
  { value: 'yaml', label: 'YAML' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'sql', label: 'SQL' },
  { value: 'bash', label: 'Bash' },
  { value: 'powershell', label: 'PowerShell' },
  { value: 'dockerfile', label: 'Dockerfile' },
  { value: 'plain', label: 'Plain Text' },
]

export default function CodeBlockDialog({
  isOpen,
  onClose,
  onInsert,
  onUpdate,
  initialCode = '',
  initialLanguage = 'javascript',
  isEditMode = false,
}: CodeBlockDialogProps) {
  const [code, setCode] = React.useState(initialCode)
  const [language, setLanguage] = React.useState(initialLanguage)
  const [visualLineCounts, setVisualLineCounts] = React.useState<number[]>([])

  const textareaRef = React.useRef<HTMLTextAreaElement>(null)
  const codeLineRefs = React.useRef<(HTMLDivElement | null)[]>([])

  React.useEffect(() => {
    if (isOpen) {
      setCode(initialCode)
      setLanguage(initialLanguage)
    }
  }, [isOpen, initialCode, initialLanguage])

  React.useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  const handleInsert = () => {
    if (code.trim()) {
      if (isEditMode && onUpdate) {
        onUpdate(code.trim(), language)
      } else {
        onInsert(code.trim(), language)
      }
      onClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleInsert()
    }
  }

  const [highlightedLines, setHighlightedLines] = React.useState<string[]>([])

  React.useEffect(() => {
    const highlightCode = async () => {
      if (!code || language === 'plain') {
        const lines = code.split('\n')
        setHighlightedLines(lines)
        return
      }

      try {
        const hljs = (await import('highlight.js')).default

        const languageMap: Record<string, string> = {
          csharp: 'csharp',
          cpp: 'cpp',
          powershell: 'powershell',
          dockerfile: 'dockerfile',
          plain: 'plaintext',
        }

        const hljsLanguage = languageMap[language] || language

        // Split code into lines first to preserve line structure
        const codeLines = code.split('\n')
        const highlightedLinesArray: string[] = []

        if (hljsLanguage && hljs.getLanguage(hljsLanguage)) {
          // Highlight each line separately to preserve line structure
          for (const line of codeLines) {
            try {
              const highlighted = hljs.highlight(line, { language: hljsLanguage })
              highlightedLinesArray.push(highlighted.value)
            } catch {
              // If highlighting a single line fails, escape it
              const escaped = line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
              highlightedLinesArray.push(escaped)
            }
          }
        } else {
          // Fallback: apply basic highlighting if language not supported
          for (const line of codeLines) {
            let highlighted = line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

            if (language === 'javascript' || language === 'typescript') {
              highlighted = highlighted.replace(
                /\b(const|let|var|function|if|else|for|while|return|class|interface|import|export|async|await|try|catch|finally)\b/g,
                '<span class="hljs-keyword">$1</span>'
              )
              highlighted = highlighted.replace(
                /(["'`])((?:\\.|(?!\1)[^\\])*?)\1/g,
                '<span class="hljs-string">$1$2$1</span>'
              )
              highlighted = highlighted.replace(/(\/\/.*$|\/\*[\s\S]*?\*\/)/gm, '<span class="hljs-comment">$1</span>')
              highlighted = highlighted.replace(/\b(\d+\.?\d*)\b/g, '<span class="hljs-number">$1</span>')
            } else if (language === 'python') {
              highlighted = highlighted.replace(
                /\b(def|class|if|else|elif|for|while|import|from|return|True|False|None|try|except|finally)\b/g,
                '<span class="hljs-keyword">$1</span>'
              )
              highlighted = highlighted.replace(
                /(["'`])((?:\\.|(?!\1)[^\\])*?)\1/g,
                '<span class="hljs-string">$1$2$1</span>'
              )
              highlighted = highlighted.replace(/(#.*$)/gm, '<span class="hljs-comment">$1</span>')
              highlighted = highlighted.replace(/\b(\d+\.?\d*)\b/g, '<span class="hljs-number">$1</span>')
            }
            highlightedLinesArray.push(highlighted)
          }
        }
        setHighlightedLines(highlightedLinesArray)
      } catch (error) {
        // Fallback to plain text if highlight.js fails
        const lines = code.split('\n')
        setHighlightedLines(lines)
      }
    }

    highlightCode()
  }, [code, language])

  // Calculate visual line breaks for wrapped lines
  React.useEffect(() => {
    if (!code) {
      setVisualLineCounts([])
      codeLineRefs.current = []
      return
    }

    const calculateVisualLines = () => {
      const LINE_HEIGHT = 19.5 // From CSS: min-height: 19.5px
      const codeLines = code.split('\n')
      const counts: number[] = []

      codeLines.forEach((_, index) => {
        const ref = codeLineRefs.current[index]
        if (ref) {
          const height = ref.offsetHeight
          const visualLines = Math.max(1, Math.round(height / LINE_HEIGHT))
          counts.push(visualLines)
        } else {
          counts.push(1)
        }
      })

      setVisualLineCounts(counts)
    }

    // Calculate after a short delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(calculateVisualLines, 100)

    // Recalculate on window resize
    const handleResize = () => {
      calculateVisualLines()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', handleResize)
    }
  }, [code, highlightedLines])

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Code Block' : 'Code Block Editor'}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 overflow-y-scroll overflow-hidden">
          {/* Left side - Editor */}
          <div className="flex flex-col space-y-4 px-2">
            <div className="space-y-2">
              <Label htmlFor="language-select">Programming Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a language" />
                </SelectTrigger>
                <SelectContent>
                  {supportedLanguages.map(lang => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="code-input">Code</Label>
              <Textarea
                ref={textareaRef}
                id="code-input"
                value={code}
                onChange={e => setCode(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your code here..."
                className="min-h-[300px] font-mono text-sm"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">Tip: Use Ctrl+Enter to insert quickly</p>
            </div>
          </div>

          {/* Right side - Preview */}
          <div className="flex flex-col space-y-4">
            <div className="flex items-center justify-between">
              <Label>Preview</Label>
              <span className="text-xs text-gray-500">{language}</span>
            </div>

            <Card className="flex-1">
              <CardContent className="p-0 h-full">
                <div className="min-h-[300px] h-full overflow-auto">
                  {code ? (
                    <div className="code-preview-container">
                      <div className="line-numbers">
                        {(() => {
                          const codeLines = code.split('\n')
                          let cumulativeLineNumber = 1
                          return codeLines.flatMap((_, logicalIndex) => {
                            const visualLineCount = visualLineCounts[logicalIndex] || 1
                            const lineNumbers = Array.from({ length: visualLineCount }, (_, visualIndex) => {
                              const lineNumber = cumulativeLineNumber + visualIndex
                              return (
                                <div key={`${logicalIndex}-${visualIndex}`} className="line-number">
                                  {lineNumber}
                                </div>
                              )
                            })
                            cumulativeLineNumber += visualLineCount
                            return lineNumbers
                          })
                        })()}
                      </div>
                      <div className="code-content-wrapper">
                        {code.split('\n').map((_, index) => {
                          const codeLines = code.split('\n')
                          // Ensure refs array is large enough
                          if (codeLineRefs.current.length < codeLines.length) {
                            codeLineRefs.current = Array(codeLines.length).fill(null)
                          }
                          return (
                            <div
                              key={index}
                              ref={el => {
                                codeLineRefs.current[index] = el
                              }}
                              className="code-line"
                            >
                              <span
                                className="hljs"
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeHtmlValue(highlightedLines[index] || codeLines[index] || '&nbsp;'),
                                }}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-400 text-center h-full flex items-center justify-center">
                      <div>
                        <div className="text-4xl mb-2">{'</>'}</div>
                        <div>Code preview will appear here</div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleInsert} disabled={!code.trim()}>
            {isEditMode ? 'Update Code Block' : 'Insert Code Block'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Syntax highlighting styles */}
      <style jsx global>{`
        .code-preview-container {
          display: flex !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
        }

        .line-numbers {
          padding: 8px 8px 8px 16px !important;
          background: #21252b !important;
          color: #5c6370 !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          text-align: right !important;
          user-select: none !important;
          border-right: 1px solid #444 !important;
          min-width: 40px !important;
          flex-shrink: 0 !important;
        }

        .line-number {
          display: block !important;
          height: 19.5px !important;
          line-height: 1.5 !important;
        }

        .code-content-wrapper {
          flex: 1 !important;
          margin: 0 !important;
          padding: 8px 0 !important;
          background: #282c34 !important;
          overflow-x: auto !important;
        }

        .code-line {
          min-height: 19.5px !important;
          line-height: 1.5 !important;
          padding: 0 8px !important;
          color: #abb2bf !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          font-size: 13px !important;
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
        }

        .code-content {
          margin: 0 !important;
          padding: 8px !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          color: #abb2bf !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          overflow: auto !important;
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
          flex: 1 !important;
        }

        .hljs {
          background: #282c34 !important;
          color: #abb2bf !important;
        }

        .hljs-keyword,
        .hljs-selector-tag,
        .hljs-built_in,
        .hljs-name {
          color: #c678dd !important;
        }

        .hljs-string,
        .hljs-attr {
          color: #98c379 !important;
        }

        .hljs-comment,
        .hljs-quote {
          color: #5c6370 !important;
          font-style: italic !important;
        }

        .hljs-number,
        .hljs-literal {
          color: #d19a66 !important;
        }

        .hljs-function,
        .hljs-title {
          color: #61afef !important;
        }

        .hljs-variable,
        .hljs-params {
          color: #e06c75 !important;
        }

        .hljs-type,
        .hljs-class {
          color: #e5c07b !important;
        }

        .hljs-operator {
          color: #56b6c2 !important;
        }

        .hljs-property {
          color: #61afef !important;
        }

        .hljs-tag {
          color: #e06c75 !important;
        }

        .hljs-selector-tag {
          color: #e06c75 !important;
        }
      `}</style>
    </Dialog>
  )
}
